import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { useAppDownload } from '@/context/AppDownloadContext';
import {
  socialBadges,
  visibilityConfigs,
  usersWithBadges,
  badgeDashboardStats,
  getBadgesByCategory,
  getBadgesByTier,
  getTopBadgedUsers,
  getBadgeById,
  type SocialBadge,
  type SocialBadgeId,
  type VisibilityLocation,
} from '@/mocks/rfwSocialBadges';

function BadgeCard({ badge, size = 'md' }: { badge: SocialBadge; size?: 'lg' | 'md' | 'sm' }) {
  if (size === 'sm') {
    return (
      <div className={`flex items-center gap-2.5 px-3 py-2 rounded-xl ${badge.bgColor} border ${badge.borderColor} transition-all duration-200`}>
        <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${badge.gradient} flex items-center justify-center shadow-sm ${badge.glowColor}`}>
          <i className={`${badge.icon} text-white text-sm`}></i>
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-[10px] font-bold ${badge.textColor} truncate`}>{badge.name}</p>
          <p className="text-[8px] text-foreground-500">{badge.totalHolders.toLocaleString()} usuarios</p>
        </div>
        {badge.exclusive && (
          <i className="ri-star-fill text-amber-400 text-[9px] shrink-0"></i>
        )}
      </div>
    );
  }

  if (size === 'lg') {
    return (
      <div className={`rounded-2xl overflow-hidden border ${badge.borderColor} bg-background-50 transition-all duration-300 hover:shadow-sm`}>
        <div className={`h-28 relative bg-gradient-to-br ${badge.gradient} flex items-center justify-center`}>
          <div className={`absolute inset-0 bg-gradient-to-br ${badge.gradient} opacity-90`}></div>
          <div className="relative z-10 flex flex-col items-center">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-1.5">
              <i className={`${badge.icon} text-white text-3xl`}></i>
            </div>
            <span className="text-white font-extrabold text-sm drop-shadow-sm">{badge.name}</span>
          </div>
          {badge.exclusive && (
            <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-[9px] font-bold">
              <i className="ri-star-fill mr-0.5 text-[8px]"></i>EXCLUSIVA
            </div>
          )}
          <div className="absolute bottom-2 right-3 text-white/70 text-[9px] font-medium">
            {badge.totalHolders.toLocaleString()}
          </div>
        </div>
        <div className="p-4">
          <p className="text-xs text-foreground-600 leading-relaxed mb-3 line-clamp-2">{badge.description}</p>
          <div className={`text-[10px] font-semibold ${badge.textColor} mb-3`}>
            ¿Cómo obtenerla? {badge.howToEarn}
          </div>
          <div className="flex flex-wrap gap-1">
            {badge.visibility.slice(0, 4).map((v) => {
              const vis = visibilityConfigs.find((vc) => vc.location === v);
              return vis ? (
                <span key={v} className="px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[8px] flex items-center gap-0.5">
                  <i className={`${vis.icon} text-[7px]`}></i>{vis.label}
                </span>
              ) : null;
            })}
            {badge.visibility.length > 4 && (
              <span className="px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-400 text-[8px]">+{badge.visibility.length - 4}</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border ${badge.borderColor} bg-background-50 p-4 transition-all duration-300 hover:border-foreground-300/50`}>
      <div className="flex items-start gap-3.5">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${badge.gradient} flex items-center justify-center shadow-sm shrink-0 ${badge.glowColor}`}>
          <i className={`${badge.icon} text-white text-lg`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className={`text-sm font-bold ${badge.textColor}`}>{badge.name}</h4>
            {badge.exclusive && (
              <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold flex items-center gap-0.5">
                <i className="ri-star-fill text-[7px]"></i>EXCLUSIVA
              </span>
            )}
          </div>
          <p className="text-[11px] text-foreground-500 leading-relaxed mb-2.5 line-clamp-2">{badge.description}</p>
          <div className={`text-[10px] font-medium ${badge.textColor} bg-background-50 rounded-lg p-2 mb-2.5 border border-background-200/70`}>
            <i className="ri-lightbulb-line mr-1"></i>{badge.howToEarn}
          </div>
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-1">
              {badge.visibility.map((v) => {
                const vis = visibilityConfigs.find((vc) => vc.location === v);
                return vis ? (
                  <span key={v} className={`px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[8px] flex items-center gap-0.5`}>
                    <i className={`${vis.icon} text-[7px]`}></i>{vis.label}
                  </span>
                ) : null;
              })}
            </div>
            <span className="text-[9px] text-foreground-400 shrink-0 ml-2">{badge.totalHolders.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function UserBadgeLine({ user, position }: { user: typeof usersWithBadges[0]; position: number }) {
  const highlightBadge = getBadgeById(user.highlightBadge);
  const isEven = position % 2 === 0;

  return (
    <div className={`flex items-center gap-3 px-4 py-3 ${isEven ? 'bg-background-50' : 'bg-background-50/70'}`}>
      <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
        <span className="text-[10px] font-bold text-foreground-500">#{position}</span>
      </div>
      <img src={user.photoUrl} alt={user.name} className="w-9 h-9 rounded-lg object-cover object-top shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-foreground-950 truncate">{user.name}</span>
          <span className="text-[10px] text-foreground-500">{user.username}</span>
        </div>
        <div className="text-[9px] text-foreground-400">{user.radioFreq}</div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {user.badgeIds.slice(0, 5).map((bid) => {
          const b = getBadgeById(bid);
          if (!b) return null;
          return (
            <div key={bid} className={`w-6 h-6 rounded-md bg-gradient-to-r ${b.gradient} flex items-center justify-center shadow-sm ${b.glowColor}`}>
              <i className={`${b.icon} text-white text-[10px]`}></i>
            </div>
          );
        })}
        {user.badgeIds.length > 5 && (
          <span className="text-[9px] text-foreground-400 font-bold ml-0.5">+{user.badgeIds.length - 5}</span>
        )}
      </div>
    </div>
  );
}

export default function InsigniasPage() {
  const { openModal: openDownloadModal } = useAppDownload();
  const [activeView, setActiveView] = useState<'galeria' | 'visibilidad' | 'usuarios'>('galeria');
  const [selectedCategory, setSelectedCategory] = useState<SocialBadge['category'] | 'todas'>('todas');
  const [selectedTier, setSelectedTier] = useState<SocialBadge['tier'] | 'todas'>('todas');
  const [expandedBadgeId, setExpandedBadgeId] = useState<SocialBadgeId | null>(null);

  const filteredBadges = socialBadges.filter((b) => {
    if (selectedCategory !== 'todas' && b.category !== selectedCategory) return false;
    if (selectedTier !== 'todas' && b.tier !== selectedTier) return false;
    return true;
  });

  const topUsers = getTopBadgedUsers(10);

  const views = [
    { id: 'galeria' as const, label: 'Galería', icon: 'ri-layout-grid-line' },
    { id: 'visibilidad' as const, label: 'Visibilidad', icon: 'ri-eye-line' },
    { id: 'usuarios' as const, label: 'Usuarios', icon: 'ri-group-line' },
  ];

  const categories: { id: SocialBadge['category'] | 'todas'; label: string; icon: string; color: string }[] = [
    { id: 'todas', label: 'Todas', icon: 'ri-apps-line', color: 'text-foreground-600' },
    { id: 'participacion', label: 'Participación', icon: 'ri-flashlight-line', color: 'text-emerald-600' },
    { id: 'estatus', label: 'Estatus', icon: 'ri-shield-star-line', color: 'text-violet-600' },
    { id: 'logro', label: 'Logro', icon: 'ri-trophy-line', color: 'text-amber-600' },
    { id: 'rol', label: 'Rol', icon: 'ri-user-star-line', color: 'text-indigo-600' },
  ];

  const tiers: { id: SocialBadge['tier'] | 'todas'; label: string }[] = [
    { id: 'todas', label: 'Todos los niveles' },
    { id: 'basico', label: 'Básico' },
    { id: 'intermedio', label: 'Intermedio' },
    { id: 'avanzado', label: 'Avanzado' },
    { id: 'elite', label: 'Élite' },
  ];

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <div className="max-w-5xl mx-auto px-4 md:px-6 pb-10">

          {/* Hero */}
          <div className="bg-background-50 rounded-2xl overflow-hidden">
            <div className="relative h-40 md:h-48 bg-gradient-to-br from-amber-400/15 via-rose-400/10 to-fuchsia-400/10 overflow-hidden">
              <div className="absolute inset-0 bg-background-50/30"></div>
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-amber-400/10 blur-3xl"></div>
              <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-fuchsia-400/10 blur-3xl"></div>

              <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-amber-400 via-rose-400 to-fuchsia-500 flex items-center justify-center mb-3">
                  <i className="ri-award-fill text-background-50 text-2xl"></i>
                </div>
                <h1 className="text-xl md:text-2xl font-black text-foreground-950 mb-1">Insignias y Estatus Sociales</h1>
                <p className="text-sm text-foreground-500">{badgeDashboardStats.totalBadges} insignias · {badgeDashboardStats.totalAssignments} asignaciones · {badgeDashboardStats.usersWithBadges} usuarios</p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-background-200">
              {[
                { label: 'Insignias', value: badgeDashboardStats.totalBadges, icon: 'ri-award-line', color: 'text-amber-500' },
                { label: 'Asignaciones', value: badgeDashboardStats.totalAssignments, icon: 'ri-shield-star-line', color: 'text-rose-500' },
                { label: 'Más Común', value: badgeDashboardStats.mostCommonBadge, sub: `${badgeDashboardStats.mostCommonBadgeCount} usuarios`, icon: 'ri-group-line', color: 'text-sky-500' },
                { label: 'Más Rara', value: badgeDashboardStats.rarestBadge, sub: `Solo ${badgeDashboardStats.rarestBadgeCount}`, icon: 'ri-star-line', color: 'text-fuchsia-500' },
              ].map((stat) => (
                <div key={stat.label} className="px-3 py-4 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <i className={`${stat.icon} ${stat.color} text-xs`}></i>
                    <span className="text-[10px] text-foreground-500">{stat.label}</span>
                  </div>
                  <div className="text-sm font-bold text-foreground-950">{stat.value}</div>
                  {stat.sub && <div className="text-[10px] text-foreground-400 mt-0.5">{stat.sub}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* View Tabs */}
          <div className="mt-5 flex items-center justify-center">
            <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
              {views.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setActiveView(v.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeView === v.id
                      ? 'bg-amber-500 text-white shadow-sm'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${v.icon} text-[10px]`}></i>
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {/* Galería View */}
          {activeView === 'galeria' && (
            <>
              {/* Category Filters */}
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      selectedCategory === cat.id
                        ? 'bg-foreground-900 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:bg-background-100 hover:text-foreground-800 border border-background-200/70'
                    }`}
                  >
                    <i className={`${cat.icon} ${selectedCategory === cat.id ? 'text-background-50' : cat.color}`}></i>
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Tier sub-filter */}
              <div className="mt-3 flex flex-wrap justify-center gap-1.5">
                {tiers.map((tier) => (
                  <button
                    key={tier.id}
                    onClick={() => setSelectedTier(tier.id)}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap ${
                      selectedTier === tier.id
                        ? 'bg-background-100 text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {tier.label}
                  </button>
                ))}
              </div>

              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-foreground-400">{filteredBadges.length} insignias</span>
                <span className="text-[10px] text-foreground-400">
                  {badgeDashboardStats.exclusiveBadges} exclusivas · Promedio {badgeDashboardStats.averageBadgesPerUser} por usuario
                </span>
              </div>

              {/* Badge Grid */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {filteredBadges.map((badge) => (
                  <div key={badge.id} onClick={() => setExpandedBadgeId(expandedBadgeId === badge.id ? null : badge.id)} className="cursor-pointer">
                    <BadgeCard badge={badge} size={expandedBadgeId === badge.id ? 'lg' : 'md'} />
                  </div>
                ))}
              </div>

              {/* Empty state */}
              {filteredBadges.length === 0 && (
                <div className="mt-4 bg-background-50 rounded-2xl p-10 text-center border border-background-200/70">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                    <i className="ri-search-line text-foreground-400 text-xl"></i>
                  </div>
                  <p className="text-sm font-bold text-foreground-700 mb-1">Sin insignias</p>
                  <p className="text-xs text-foreground-500">No hay insignias con esos filtros.</p>
                </div>
              )}

              {/* How badges work */}
              <div className="mt-8 p-6 rounded-2xl bg-background-50 border border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-5">
                  <i className="ri-lightbulb-line text-amber-500"></i>
                  ¿Cómo funcionan las insignias?
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center shrink-0">
                      <i className="ri-award-line text-background-50"></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-foreground-950 mb-1">Reconocimiento Visible</p>
                      <p className="text-[11px] text-foreground-500 leading-relaxed">Cada insignia aparece en tu perfil, comentarios, cabina y más. Todos saben quién sos.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-rose-500 flex items-center justify-center shrink-0">
                      <i className="ri-palette-line text-background-50"></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-foreground-950 mb-1">Identidad de Color</p>
                      <p className="text-[11px] text-foreground-500 leading-relaxed">Cada insignia tiene su propio color, degradado y glow. Se reconocen al instante.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0">
                      <i className="ri-arrow-up-line text-background-50"></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-foreground-950 mb-1">Progresión Natural</p>
                      <p className="text-[11px] text-foreground-500 leading-relaxed">De Nuevo a Leyenda, las insignias reflejan tu evolución en la plataforma.</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Visibilidad View */}
          {activeView === 'visibilidad' && (
            <div className="mt-6 space-y-5">
              {/* Visibility Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {visibilityConfigs.map((vis) => {
                  const badgesHere = socialBadges.filter((b) => b.visibility.includes(vis.location));
                  return (
                    <div key={vis.location} className="p-5 rounded-2xl bg-background-50 border border-background-200/70 transition-all duration-200 hover:border-amber-200/50">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                          <i className={`${vis.icon} text-amber-600 text-base`}></i>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-foreground-950">{vis.label}</p>
                          <p className="text-[10px] text-foreground-500">{vis.activeBadgeCount} insignias activas</p>
                        </div>
                      </div>
                      <p className="text-[11px] text-foreground-500 leading-relaxed mb-3">{vis.description}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {badgesHere.slice(0, 6).map((b) => (
                          <span key={b.id} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-semibold ${b.bgColor} ${b.textColor}`}>
                            <i className={`${b.icon} text-[8px]`}></i>
                            {b.shortName}
                          </span>
                        ))}
                        {badgesHere.length > 6 && (
                          <span className="px-2 py-1 rounded-full text-[9px] text-foreground-500 bg-background-100">+{badgesHere.length - 6}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Badge visibility matrix */}
              <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70 overflow-x-auto">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                  <i className="ri-table-line text-amber-500"></i>
                  Matriz de Visibilidad
                </h3>
                <div className="min-w-[600px]">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-background-200">
                        <th className="py-2 px-3 text-[10px] font-bold text-foreground-500 uppercase">Insignia</th>
                        {visibilityConfigs.map((v) => (
                          <th key={v.location} className="py-2 px-3 text-center text-[10px] font-bold text-foreground-500 uppercase">{v.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {socialBadges.map((badge) => (
                        <tr key={badge.id} className="border-b border-background-100">
                          <td className="py-2.5 px-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-6 h-6 rounded-md bg-gradient-to-r ${badge.gradient} flex items-center justify-center`}>
                                <i className={`${badge.icon} text-white text-[9px]`}></i>
                              </div>
                              <span className={`text-[11px] font-semibold ${badge.textColor}`}>{badge.name}</span>
                            </div>
                          </td>
                          {visibilityConfigs.map((v) => {
                            const active = badge.visibility.includes(v.location);
                            return (
                              <td key={v.location} className="py-2.5 px-3 text-center">
                                {active ? (
                                  <div className="flex items-center justify-center">
                                    <div className={`w-5 h-5 rounded-md bg-gradient-to-r ${badge.gradient} flex items-center justify-center shadow-sm ${badge.glowColor}`}>
                                      <i className="ri-check-line text-white text-[9px]"></i>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-foreground-300 text-xs">—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Usuarios View */}
          {activeView === 'usuarios' && (
            <div className="mt-6 space-y-5">
              {/* Top Badged Users */}
              <div className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden">
                <div className="px-5 py-4 border-b border-background-200">
                  <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                    <i className="ri-group-line text-amber-500"></i>
                    Usuarios con Más Insignias
                  </h3>
                  <p className="text-[10px] text-foreground-500 mt-0.5">Top {topUsers.length} · {badgeDashboardStats.usersWithBadges} usuarios badgeados en total</p>
                </div>
                <div>
                  {topUsers.map((user, idx) => (
                    <UserBadgeLine key={user.id} user={user} position={idx + 1} />
                  ))}
                </div>
              </div>

              {/* Badge Distribution */}
              <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                  <i className="ri-bar-chart-line text-amber-500"></i>
                  Distribución de Insignias
                </h3>
                <div className="space-y-3">
                  {[...socialBadges].sort((a, b) => b.totalHolders - a.totalHolders).map((badge) => {
                    const maxHolders = socialBadges[0].totalHolders;
                    const barWidth = Math.round((badge.totalHolders / maxHolders) * 100);
                    return (
                      <div key={badge.id} className="flex items-center gap-3">
                        <div className={`w-7 h-7 rounded-lg bg-gradient-to-r ${badge.gradient} flex items-center justify-center shrink-0 shadow-sm ${badge.glowColor}`}>
                          <i className={`${badge.icon} text-white text-[10px]`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-[11px] font-semibold ${badge.textColor}`}>{badge.name}</span>
                            <span className="text-[10px] font-bold text-foreground-700">{badge.totalHolders.toLocaleString()}</span>
                          </div>
                          <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full bg-gradient-to-r ${badge.gradient} transition-all duration-700 ease-out`}
                              style={{ width: `${barWidth}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-8 bg-background-50 rounded-2xl p-8 text-center border border-background-200/70">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-400 via-rose-400 to-fuchsia-500 flex items-center justify-center">
              <i className="ri-rocket-line text-background-50 text-2xl"></i>
            </div>
            <h3 className="text-lg font-bold text-foreground-950 mb-2">Empezá a ganar insignias</h3>
            <p className="text-sm text-foreground-500 mb-5 max-w-md mx-auto">
              Cada insignia es un reflejo de tu historia en RFW. Participá, crecé y dejá tu marca en la comunidad.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={openDownloadModal}
                className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line mr-1.5"></i>
                Descargar App RFW
              </button>
              <Link
                to="/gamificacion"
                className="px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-flashlight-line mr-1.5"></i>
                Gamificación
              </Link>
            </div>
          </div>

          {/* Cross-links */}
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <Link to="/top-fans" className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
                  <i className="ri-star-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Top Fans</h4>
                  <p className="text-xs text-foreground-500">Ranking de actividad</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/vip-rfw" className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-500 flex items-center justify-center">
                  <i className="ri-vip-diamond-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Club VIP</h4>
                  <p className="text-xs text-foreground-500">Estatus exclusivo</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/rankings" className="flex-1 bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-sky-500 flex items-center justify-center">
                  <i className="ri-bar-chart-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Rankings</h4>
                  <p className="text-xs text-foreground-500">Tablas de posiciones</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
          </div>

          {/* Regla Final */}
          <div className="mt-6 p-6 rounded-2xl bg-amber-100/70 border border-amber-200/50 text-center">
            <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center mx-auto mb-3">
              <i className="ri-eye-line text-lg text-white"></i>
            </div>
            <p className="text-sm font-bold text-foreground-950 mb-1">
              Las insignias deben ser fáciles de reconocer.
            </p>
            <p className="text-xs text-foreground-600 max-w-lg mx-auto">
              Cada insignia tiene su propio color, degradado y glow visual. Están diseñadas para que cualquier usuario pueda identificarlas al instante sin necesidad de leer — solo con ver el color y el ícono ya sabés de qué se trata.
            </p>
          </div>

        </div>
      </main>

      <Footer />
      <div className="md:hidden h-20"></div>
    </div>
  );
}